import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { buildAssessmentFormContext } from "./assistantPageContext";
import toast, { Toaster } from "react-hot-toast";
import {
  fetchAssessmentForm,
  updateAssessmentQuestion,
  updateLiteQuestionSelection,
  addAssessmentQuestion,
  deleteAssessmentQuestion,
  reorderAssessmentQuestions,
  fetchSurveyTemplates,
  updateSurveyTemplate,
  activateSurveyTemplate,
  fetchCompanies,
  fetchAssessmentFormats,
  fetchWorkspace,
} from "../api/adminClient";
import { workspaceLiteInviteAvailable } from "./workspaceProductSettings";
import { isAbortError } from "../utils/fetchAbort";
import { withDisplayNumbers } from "../utils/questionNumbers";
import V1StyleAssessmentLayout, {
  V1StyleQuestionBlocks,
} from "../components/V1StyleAssessmentLayout";
import EditQuestionModal from "./EditQuestionModal";
import AddQuestionModal from "./AddQuestionModal";
import LiteQuestionPicker from "./LiteQuestionPicker";
import BulkQuestionUploadModal from "./BulkQuestionUploadModal";
import AssessmentEditorModal from "./AssessmentEditorModal";
import SketchModal, { canUseSketch, DraftWithAiButton } from "./SketchModal";

const variantToggle = (variant, setVariant) => (
  <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shrink-0">
    <button
      type="button"
      className={`px-4 py-2 text-sm font-medium rounded-md ${
        variant === "lite"
          ? "bg-[#004F73] text-white"
          : "text-gray-700 hover:bg-gray-50"
      }`}
      onClick={() => setVariant("lite")}
    >
      LITE
    </button>
    <button
      type="button"
      className={`px-4 py-2 text-sm font-medium rounded-md ${
        variant === "full"
          ? "bg-[#004F73] text-white"
          : "text-gray-700 hover:bg-gray-50"
      }`}
      onClick={() => setVariant("full")}
    >
      Full
    </button>
  </div>
);

function sameIdSet(a = [], b = []) {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

export default function AssessmentFormPage({
  embedded = false,
  templateId: initialTemplateId = "",
  organizationId: initialOrgId = "",
  preferFiveXTemplate = false,
  onAssessmentCreated,
}) {
  const { setAdminAssistantContext } = useOutletContext() || {};

  // ── Template list (tab bar) ───────────────────────────────────────────────
  const [templates, setTemplates] = useState([]);
  const [activeTemplateId, setActiveTemplateId] = useState(initialTemplateId);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create"); // create | edit
  const [editorTemplateId, setEditorTemplateId] = useState("");
  const [renamingId, setRenamingId] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [savingRename, setSavingRename] = useState(false);
  const [activatingId, setActivatingId] = useState("");
  const [templatesReady, setTemplatesReady] = useState(false);
  const [scopeOrgId, setScopeOrgId] = useState(initialOrgId || "");
  const [companies, setCompanies] = useState([]);
  const [formats, setFormats] = useState([]);
  const renameInputRef = useRef(null);
  const systemFormatId = useMemo(
    () => formats.find((f) => f.is_system)?.id || "",
    [formats]
  );

  const [formNonce, setFormNonce] = useState(0);

  const loadTemplates = useCallback(async (preferId = "") => {
    try {
      const res = await fetchSurveyTemplates(scopeOrgId);
      const list = (res.data?.templates || []).filter((t) => !t.is_archived);
      setTemplates(list);
      setActiveTemplateId((prev) => {
        if (preferId && list.find((t) => t.id === preferId)) return preferId;
        if (prev && list.find((t) => t.id === prev)) return prev;
        const live = list.find((t) => t.is_active);
        return live?.id || list[0]?.id || "";
      });
      if (!list.length) setLoading(false);
      if (preferId) setFormNonce((n) => n + 1);
    } catch (e) {
      setTemplates([]);
      setActiveTemplateId("");
      setLoading(false);
      if (e.httpStatus === 401 || e.code === 401) return;
      toast.error(e.message || "Could not load templates");
    } finally {
      setTemplatesReady(true);
    }
  }, [scopeOrgId]);

  useEffect(() => {
    fetchCompanies()
      .then((res) => setCompanies(res.data?.companies || []))
      .catch(() => setCompanies([]));
    fetchAssessmentFormats()
      .then((res) => setFormats(res.data?.formats || []))
      .catch(() => setFormats([]));
    fetchWorkspace()
      .then((res) => setWorkspace(res.data?.workspace || res.data || null))
      .catch(() => setWorkspace(null));
  }, []);

  useEffect(() => {
    setTemplatesReady(false);
    setLoading(true);
    setActiveTemplateId("");
    setTemplates([]);
    setFullSteps([]);
    setLiteSteps([]);
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (renamingId) renameInputRef.current?.focus();
  }, [renamingId]);

  const openCreateEditor = () => {
    setEditorMode("create");
    setEditorTemplateId("");
    setEditorOpen(true);
  };

  const fiveXPromptOpened = useRef(false);
  useEffect(() => {
    if (!preferFiveXTemplate || !templatesReady || fiveXPromptOpened.current) return;
    fiveXPromptOpened.current = true;
    openCreateEditor();
  }, [preferFiveXTemplate, templatesReady]);

  const openEditEditor = (tpl) => {
    if (!tpl?.id) return;
    setActiveTemplateId(tpl.id);
    setEditorMode("edit");
    setEditorTemplateId(tpl.id);
    setEditorOpen(true);
  };

  const handleEditorSaved = async ({ id, soft, deleted } = {}) => {
    fetchAssessmentFormats()
      .then((res) => setFormats(res.data?.formats || []))
      .catch(() => {});
    if (deleted) {
      await loadTemplates("");
      return;
    }
    if (id) setActiveTemplateId(id);
    await loadTemplates(id || "");
    if (!soft && id) {
      // After create, keep editor closed; reload preview
      setFormNonce((n) => n + 1);
      onAssessmentCreated?.();
    } else if (soft) {
      setFormNonce((n) => n + 1);
    }
  };

  const startRename = (tpl) => {
    setEditorOpen(false);
    setRenamingId(tpl.id);
    setRenameValue(tpl.name || "");
  };

  const cancelRename = () => {
    if (savingRename) return;
    setRenamingId("");
    setRenameValue("");
  };

  const handleRenameTemplate = async () => {
    const name = renameValue.trim();
    if (!renamingId || !name) return;
    const current = templates.find((t) => t.id === renamingId);
    if (current && name === current.name) {
      cancelRename();
      return;
    }
    setSavingRename(true);
    try {
      await updateSurveyTemplate(renamingId, { name });
      toast.success("Template renamed");
      setRenamingId("");
      setRenameValue("");
      await loadTemplates();
    } catch (e) {
      toast.error(e.message || "Could not rename template");
    } finally {
      setSavingRename(false);
    }
  };

  /** Click a version: preview it and open the editor modal. */
  const handleSelectTemplate = (tpl) => {
    openEditEditor(tpl);
  };

  const handleActivateTemplate = async (tpl) => {
    if (!tpl?.id || tpl.inherited) return;
    const savedCount = tpl.question_count ?? 0;
    const liveCount =
      tpl.id === activeTemplateId
        ? fullSteps.reduce((n, s) => n + (s.questions || []).length, 0)
        : 0;
    if (Math.max(savedCount, liveCount) === 0) {
      toast.error("Add at least one question before activating this assessment.");
      return;
    }
    setActivatingId(tpl.id);
    try {
      await activateSurveyTemplate(tpl.id);
      toast.success("Assessment set as active");
      await loadTemplates(tpl.id);
      setFormNonce((n) => n + 1);
    } catch (e) {
      toast.error(e.message || "Could not activate");
    } finally {
      setActivatingId("");
    }
  };

  // ── Assessment form state ─────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [fullSteps, setFullSteps] = useState([]);
  const [liteSteps, setLiteSteps] = useState([]);
  const [surveyId, setSurveyId] = useState("");
  const [savedLiteIds, setSavedLiteIds] = useState([]);
  const [draftLiteIds, setDraftLiteIds] = useState([]);
  const [defaultLiteIds, setDefaultLiteIds] = useState([]);
  const [variant, setVariant] = useState("full");
  const [currentStep, setCurrentStep] = useState(1);
  const [editOpen, setEditOpen] = useState(false);
  const [editQuestionId, setEditQuestionId] = useState("");
  const [editQuestionText, setEditQuestionText] = useState("");
  const [editOriginalText, setEditOriginalText] = useState("");
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [savingLite, setSavingLite] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // Add question state
  const [addOpen, setAddOpen] = useState(false);
  const [addStep, setAddStep] = useState(1);
  const [addText, setAddText] = useState("");
  const [savingAdd, setSavingAdd] = useState(false);
  // Delete confirm state
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingQuestion, setDeletingQuestion] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [sketchOpen, setSketchOpen] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const showSketch = canUseSketch();

  const liteStudioEnabled =
    workspace != null && workspaceLiteInviteAvailable(workspace);

  useEffect(() => {
    if (!liteStudioEnabled && variant !== "full") {
      setVariant("full");
    }
  }, [liteStudioEnabled, variant]);

  const isLiteSinglePage = liteStudioEnabled && variant === "lite";
  const steps = isLiteSinglePage ? liteSteps : fullSteps;
  const liteDirty = !sameIdSet(draftLiteIds, savedLiteIds);
  const usingCompanyTemplates = Boolean(
    scopeOrgId && templates.some((t) => !t.inherited)
  );
  // Only one template may show as Active in the UI (guards against legacy DB data).
  const soleActiveTemplateId = useMemo(() => {
    const actives = templates.filter((t) => t.is_active);
    if (actives.length === 1) return actives[0].id;
    if (actives.length > 1) {
      if (actives.some((t) => t.id === activeTemplateId)) return activeTemplateId;
      return actives[0].id;
    }
    return "";
  }, [templates, activeTemplateId]);

  const load = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await fetchAssessmentForm({ signal }, activeTemplateId);
      const liteIds = res.data?.lite_question_ids || [];
      const defaults = res.data?.default_lite_question_ids || [];
      setFullSteps(withDisplayNumbers(res.data?.full_survey || []));
      setLiteSteps(withDisplayNumbers(res.data?.lite_survey || []));
      setSurveyId(res.data?.survey_id || "");
      setSavedLiteIds(liteIds);
      setDraftLiteIds(liteIds);
      setDefaultLiteIds(defaults);
      setCurrentStep(1);
    } catch (e) {
      if (isAbortError(e, signal) || e.httpStatus === 401 || e.code === 401) return;
      toast.error(e.message || "Could not load assessment form");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [activeTemplateId, formNonce]);

  useEffect(() => {
    if (!templatesReady || activeTemplateId) return;
    setLoading(false);
    setFullSteps([]);
    setLiteSteps([]);
    setSurveyId("");
    setSavedLiteIds([]);
    setDraftLiteIds([]);
    setDefaultLiteIds([]);
  }, [templatesReady, activeTemplateId]);

  // Wait for survey-templates so we fetch assessment-form with the right template_id
  // (avoids aborted/red Network entries from a premature no-id request).
  useEffect(() => {
    if (!templatesReady || !activeTemplateId) return;
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, templatesReady, activeTemplateId]);

  useEffect(() => {
    setCurrentStep(1);
  }, [variant]);

  // Reset to Full view and step 1 immediately when template changes
  useEffect(() => {
    setVariant("full");
    setCurrentStep(1);
    setFullSteps([]);
    setLiteSteps([]);
    setDeleteConfirmId(null);
  }, [activeTemplateId]);

  const stepData = steps[currentStep - 1];
  const hasNoTemplates = templatesReady && templates.length === 0;
  const loadingQuestions = loading && Boolean(activeTemplateId);
  const totalQuestionCount = useMemo(
    () =>
      fullSteps.reduce((n, s) => n + ((s.questions || []).length), 0),
    [fullSteps]
  );
  const noQuestions =
    !loadingQuestions && Boolean(activeTemplateId) && totalQuestionCount === 0;
  const editorTemplateMeta = templates.find((t) => t.id === editorTemplateId) || null;

  const assistantContext = useMemo(
    () =>
      buildAssessmentFormContext({
        fullSteps,
        liteSteps,
        variant,
        currentStep,
        surveyId,
      }),
    [fullSteps, liteSteps, variant, currentStep, surveyId]
  );

  useEffect(() => {
    if (!setAdminAssistantContext || loadingQuestions) return;
    setAdminAssistantContext(assistantContext);
  }, [assistantContext, loadingQuestions, setAdminAssistantContext]);

  useEffect(() => {
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext]);

  const openEditModal = (question) => {
    // Prefer stored DB wording so edits persist on this template document.
    const text = question.source_text || question.text || "";
    setEditQuestionId(question.id);
    setEditQuestionText(text);
    setEditOriginalText(text);
    setEditOpen(true);
  };

  const closeEditModal = () => {
    if (savingQuestion) return;
    setEditOpen(false);
    setEditQuestionId("");
    setEditQuestionText("");
    setEditOriginalText("");
  };

  const handleUpdateQuestion = async () => {
    if (!surveyId || !editQuestionId || !editQuestionText.trim()) return;

    const trimmed = editQuestionText.trim();
    if (trimmed === editOriginalText.trim()) {
      toast("No changes to save");
      closeEditModal();
      return;
    }

    setSavingQuestion(true);
    try {
      // Always persist on Full survey for this template doc; LITE is rebuilt from Full.
      const res = await updateAssessmentQuestion({
        survey_id: surveyId,
        template: "full",
        question_id: editQuestionId,
        new_text: trimmed,
      });
      toast.success(res.message || "Question updated");
      const step = currentStep;
      closeEditModal();
      await load();
      setCurrentStep(isLiteSinglePage ? 1 : step);
    } catch (e) {
      toast.error(e.message || "Could not update question");
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleSaveLiteSelection = async () => {
    if (!surveyId || draftLiteIds.length === 0) {
      toast.error("Select at least one question for LITE");
      return;
    }
    setSavingLite(true);
    try {
      const res = await updateLiteQuestionSelection({
        survey_id: surveyId,
        question_ids: draftLiteIds,
      });
      toast.success(res.message || "LITE questions saved");
      await load();
      setPickerOpen(false);
    } catch (e) {
      toast.error(e.message || "Could not save LITE selection");
    } finally {
      setSavingLite(false);
    }
  };

  const handleResetLiteSelection = async () => {
    if (!surveyId) return;
    setSavingLite(true);
    try {
      const res = await updateLiteQuestionSelection({
        survey_id: surveyId,
        reset_to_default: true,
      });
      toast.success(res.message || "LITE questions reset to default");
      await load();
    } catch (e) {
      toast.error(e.message || "Could not reset LITE selection");
    } finally {
      setSavingLite(false);
    }
  };

  const openLitePicker = () => {
    setDraftLiteIds(savedLiteIds);
    setPickerOpen(true);
  };

  const closeLitePicker = () => {
    if (savingLite) return;
    setDraftLiteIds(savedLiteIds);
    setPickerOpen(false);
  };

  // ── Add question ─────────────────────────────────────────────────────────
  const openAddModal = () => {
    setAddText("");
    setAddStep(isLiteSinglePage ? 1 : currentStep);
    setAddOpen(true);
  };

  const closeAddModal = () => {
    if (savingAdd) return;
    setAddOpen(false);
    setAddText("");
  };

  const handleAddQuestion = async () => {
    if (!surveyId || !addText.trim()) return;
    setSavingAdd(true);
    try {
      await addAssessmentQuestion({
        survey_id: surveyId,
        step: addStep,
        question_text: addText.trim(),
      });
      toast.success("Question added");
      closeAddModal();
      const step = currentStep;
      await load();
      setCurrentStep(isLiteSinglePage ? 1 : step);
    } catch (e) {
      toast.error(e.message || "Could not add question");
    } finally {
      setSavingAdd(false);
    }
  };

  // ── Delete question ───────────────────────────────────────────────────────
  const handleDeleteQuestion = (question) => {
    setDeleteConfirmId(question.id);
  };

  const confirmDeleteQuestion = async () => {
    if (!surveyId || !deleteConfirmId) return;
    setDeletingQuestion(true);
    try {
      await deleteAssessmentQuestion(surveyId, deleteConfirmId);
      toast.success("Question deleted");
      setDeleteConfirmId(null);
      const step = currentStep;
      await load();
      setCurrentStep(isLiteSinglePage ? 1 : step);
    } catch (e) {
      toast.error(e.message || "Could not delete question");
    } finally {
      setDeletingQuestion(false);
    }
  };

  // ── Move question (reorder within current step) ───────────────────────────
  const handleMoveQuestion = async (question, direction) => {
    if (!surveyId) return;
    const stepData = fullSteps[currentStep - 1];
    if (!stepData) return;
    const qs = stepData.questions || [];
    const idx = qs.findIndex((q) => q.id === question.id);
    if (idx < 0) return;
    const newIdx = direction === "up" ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= qs.length) return;

    const newOrder = [...qs];
    const [moved] = newOrder.splice(idx, 1);
    newOrder.splice(newIdx, 0, moved);

    try {
      await reorderAssessmentQuestions({
        survey_id: surveyId,
        step: stepData.step ?? currentStep,
        question_ids: newOrder.map((q) => q.id),
      });
      const step = currentStep;
      await load();
      setCurrentStep(step);
    } catch (e) {
      toast.error(e.message || "Could not reorder questions");
    }
  };

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-white pb-16"}>
      <Toaster position="top-center" />
      <div className={embedded ? "w-full" : "w-full px-6 py-6 max-w-7xl mx-auto"}>
        {!embedded ? (
          <header className="mb-6">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
              Admin
            </p>
            <h1 className="text-2xl font-bold text-gray-900">Assessment form</h1>
          </header>
        ) : null}

        {/* ── Template tab bar — always visible ────────────────────────── */}
        <div className="mb-6 pb-4 border-b border-slate-100">
          <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Assessments
            </p>
            {templatesReady && templates.length > 0 ? (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                type="button"
                onClick={openCreateEditor}
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] hover:bg-[#004F73]/5 transition-colors"
              >
                New assessment
              </button>
              {showSketch ? (
                <DraftWithAiButton onClick={() => setSketchOpen(true)} />
              ) : null}
              <button
                type="button"
                onClick={() => setBulkOpen(true)}
                title="Upload questions from CSV or Excel to update this template or create a new one"
                className="px-3 py-1.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] hover:bg-[#004F73]/5 transition-colors"
              >
                Bulk upload
              </button>
            </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">
              Company
            </label>
            <select
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004F73]/25"
              value={scopeOrgId}
              onChange={(e) => setScopeOrgId(e.target.value)}
            >
              <option value="">Workspace default (all companies)</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>
          {scopeOrgId ? (
            <p className="text-xs text-slate-500 mb-3">
              {usingCompanyTemplates
                ? "This company has its own assessment. Other companies keep the active workspace assessment."
                : "This company uses the active workspace assessment until you set a different one here."}
            </p>
          ) : (
            <p className="text-xs text-slate-500 mb-3">
              The active assessment is used by every company until you assign a different one to that company.
            </p>
          )}

          {/* Template tabs row */}
          {!templatesReady ? (
            <p className="text-sm text-slate-400">Loading templates…</p>
          ) : templates.length === 0 ? null : (
            <div className="flex items-center gap-2 flex-wrap">
              {templates.map((tpl) => {
                const isSelected = tpl.id === activeTemplateId;
                const isLive = tpl.id === soleActiveTemplateId;
                const isRenaming = renamingId === tpl.id;
                if (isRenaming) {
                  return (
                    <div key={tpl.id} className="flex items-center gap-2">
                      <input
                        ref={renameInputRef}
                        type="text"
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleRenameTemplate();
                          if (e.key === "Escape") cancelRename();
                        }}
                        className="border border-[#004F73]/40 rounded-xl px-3 py-2 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-[#004F73]/25"
                      />
                      <button
                        type="button"
                        onClick={handleRenameTemplate}
                        disabled={savingRename || !renameValue.trim()}
                        className="px-3 py-1.5 text-sm rounded-lg font-semibold text-white bg-[#004F73] hover:opacity-90 disabled:opacity-50"
                      >
                        {savingRename ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={cancelRename}
                        disabled={savingRename}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  );
                }
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => handleSelectTemplate(tpl)}
                    onDoubleClick={(e) => {
                      e.preventDefault();
                      startRename(tpl);
                    }}
                    disabled={!!activatingId}
                    title="Open editor to manage questions. Double-click to rename."
                    className={`group flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl border transition-all disabled:opacity-60 ${
                      isSelected
                        ? "bg-[#004F73] text-white border-[#004F73] shadow-md"
                        : "bg-white text-slate-600 border-slate-200 hover:border-[#004F73]/40 hover:text-[#004F73] hover:bg-[#004F73]/4"
                    }`}
                  >
                    <span>{tpl.name}</span>
                    {tpl.is_baseline ? (
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                          isSelected ? "bg-white/20 text-white" : "bg-amber-50 text-amber-800"
                        }`}
                      >
                        V1
                      </span>
                    ) : null}
                    {tpl.inherited && !tpl.is_baseline ? (
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                          isSelected ? "bg-white/20 text-white" : "bg-sky-50 text-sky-700"
                        }`}
                      >
                        Workspace
                      </span>
                    ) : null}
                    {isLive ? (
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${
                          isSelected ? "bg-emerald-400/30 text-white" : "bg-emerald-50 text-emerald-700"
                        }`}
                      >
                        Active
                      </span>
                    ) : (
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                          isSelected ? "bg-white/20 text-white/80" : "bg-slate-100 text-slate-400"
                        }`}
                      >
                        Inactive
                      </span>
                    )}
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                      isSelected ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500 group-hover:bg-[#004F73]/10"
                    }`}>
                      {activatingId === tpl.id ? "…" : tpl.question_count}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Active template info strip */}
          {activeTemplateId && templates.length > 0 && !renamingId && (
            <div className="mt-2.5 flex flex-wrap items-center gap-3 text-xs text-slate-400">
              <p>
                <span className="font-medium text-slate-500">{templates.find((t) => t.id === activeTemplateId)?.name}</span>
                {" · "}
                {templates.find((t) => t.id === activeTemplateId)?.question_count ?? 0} questions
                {liteStudioEnabled ? (
                  <>
                    {" · "}
                    {savedLiteIds.length} in LITE
                    {liteDirty ? (
                      <span className="ml-2 text-amber-600 font-medium">· unsaved LITE changes</span>
                    ) : null}
                  </>
                ) : null}
              </p>
              <button
                type="button"
                onClick={() => {
                  const tpl = templates.find((t) => t.id === activeTemplateId);
                  if (tpl) openEditEditor(tpl);
                }}
                className="font-semibold text-[#004F73] hover:underline"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => {
                  const tpl = templates.find((t) => t.id === activeTemplateId);
                  if (tpl) startRename(tpl);
                }}
                className="font-semibold text-[#004F73] hover:underline"
              >
                Rename
              </button>
              {(() => {
                const tpl = templates.find((t) => t.id === activeTemplateId);
                if (!tpl || tpl.is_active || tpl.inherited) return null;
                return (
                  <button
                    type="button"
                    onClick={() => handleActivateTemplate(tpl)}
                    disabled={!!activatingId}
                    className="font-semibold text-emerald-700 hover:underline disabled:opacity-50"
                  >
                    {activatingId === tpl.id ? "Activating…" : "Set as active"}
                  </button>
                );
              })()}
            </div>
          )}
        </div>

        {liteStudioEnabled && !hasNoTemplates ? (
          <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={openLitePicker}
              className="px-4 py-2 text-sm font-semibold rounded-full border border-[#004F73]/25 text-[#004F73] bg-white hover:bg-[#004F73]/5 shadow-sm"
            >
              Configure LITE questions
            </button>
          </div>
        ) : null}

        {liteStudioEnabled ? (
          <LiteQuestionPicker
            open={pickerOpen}
            onClose={closeLitePicker}
            fullSteps={fullSteps}
            selectedIds={draftLiteIds}
            defaultIds={defaultLiteIds}
            dirty={liteDirty}
            saving={savingLite}
            onChange={setDraftLiteIds}
            onSave={handleSaveLiteSelection}
            onReset={handleResetLiteSelection}
          />
        ) : null}

        {preferFiveXTemplate ? (
          <div className="mb-4 rounded-xl border border-[#004F73]/20 bg-[#004F73]/[0.04] px-4 py-3 text-sm text-slate-700">
            <p className="font-medium text-[#004F73]">5X Team Assessment template</p>
            <p className="mt-0.5 text-slate-600">
              Create a new assessment starting from the classic structure, then customize
              sections and questions for your workspace. You can also build a fully custom
              assessment instead.
            </p>
          </div>
        ) : null}

        {hasNoTemplates ? (
          <div className="rounded-2xl border border-dashed border-[#004F73]/25 bg-gradient-to-b from-[#004F73]/[0.04] via-white to-white px-6 py-12 sm:py-16 text-center shadow-sm">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#004F73]/10 text-[#004F73]">
              <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">
              Create your first assessment
            </h3>
            <p className="text-sm text-slate-500 max-w-lg mx-auto leading-relaxed mb-6">
              Name it, define sections (pillars), then add questions one-by-one or via bulk upload.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={openCreateEditor}
                className="px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-[#004F73] hover:opacity-90 shadow-sm"
              >
                New assessment
              </button>
              {showSketch ? (
                <DraftWithAiButton
                  onClick={() => setSketchOpen(true)}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] bg-white hover:bg-[#004F73]/5"
                />
              ) : null}
              <button
                type="button"
                onClick={() => setBulkOpen(true)}
                className="px-5 py-2.5 text-sm font-semibold rounded-lg border border-[#004F73]/30 text-[#004F73] bg-white hover:bg-[#004F73]/5"
              >
                Bulk upload
              </button>
            </div>
            <ul className="mt-8 mx-auto max-w-md text-left text-sm text-slate-500 space-y-2.5">
              <li className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#004F73]/10 text-[10px] font-bold text-[#004F73]">1</span>
                <span>Create an assessment — name, sections, and questions in one place.</span>
              </li>
              <li className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#004F73]/10 text-[10px] font-bold text-[#004F73]">2</span>
                <span>Add questions under each section in the editor, or import via bulk upload.</span>
              </li>
              <li className="flex gap-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#004F73]/10 text-[10px] font-bold text-[#004F73]">3</span>
                <span>Activate it — new invites use that assessment for your workspace.</span>
              </li>
            </ul>
          </div>
        ) : null}

        {/* Loading spinner while switching templates */}
        {loadingQuestions && (
          <div className="py-16 flex items-center justify-center">
            <p className="text-[#004F73] font-medium text-sm">Loading questions…</p>
          </div>
        )}

        {noQuestions && !loadingQuestions && !hasNoTemplates && (
          <div className="py-16 text-center rounded-xl border border-dashed border-slate-200 bg-slate-50/60">
            <p className="text-slate-600 text-sm font-medium mb-1">This assessment has no questions yet.</p>
            <p className="text-xs text-slate-400 mb-4">
              Open the editor to add questions under each section, or use bulk upload.
            </p>
            <button
              type="button"
              onClick={() => {
                const tpl = templates.find((t) => t.id === activeTemplateId);
                if (tpl) openEditEditor(tpl);
              }}
              className="px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-[#004F73] hover:opacity-90"
            >
              Add questions
            </button>
          </div>
        )}

        {!loadingQuestions && !noQuestions && !hasNoTemplates && (
        <div className="admin-survey-preview rounded-xl overflow-hidden border border-slate-200/80 shadow-sm">
          <V1StyleAssessmentLayout
            hideTopNav
            singlePageMode={isLiteSinglePage}
            headerRight={
              liteStudioEnabled ? variantToggle(variant, setVariant) : null
            }
            trackBanner={
              isLiteSinglePage ? "LITE assessment" : "Full assessment"
            }
            steps={steps}
            currentStep={isLiteSinglePage ? 1 : currentStep}
            footer={
              isLiteSinglePage ? null : (
                <>
                  <button
                    type="button"
                    onClick={() => setCurrentStep((s) => Math.max(1, s - 1))}
                    className={`px-6 md:px-8 py-2 bg-white md:text-xs text-[#004F73] rounded-full border border-[#004F73] font-medium ${
                      currentStep === 1 ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    disabled={currentStep === 1}
                  >
                    BACK
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentStep((s) => Math.min(steps.length, s + 1))
                    }
                    style={{ borderRadius: "40px" }}
                    className="px-6 md:px-8 py-2 text-base md:text-sm rounded-full font-medium text-white bg-[#004F73] hover:opacity-90 disabled:opacity-50"
                    disabled={currentStep >= steps.length}
                  >
                    {currentStep >= steps.length ? "End of form" : "Next"}
                  </button>
                </>
              )
            }
          >
            {isLiteSinglePage ? (
              steps.map((step) => (
                <section
                  key={step.step ?? step.title}
                  className="mt-8 first:mt-4 border-t border-gray-100 pt-6 first:border-t-0 first:pt-0"
                >
                  <div className="md:px-2 mb-4">
                    <p className="text-lg font-bold text-[#004F73]">
                      {step.title || `Section ${step.step}`}
                    </p>
                  </div>
                  <V1StyleQuestionBlocks
                    stepData={step}
                    answers={{}}
                    setAnswerField={() => {}}
                    readOnly
                    onEditQuestion={openEditModal}
                    onDeleteQuestion={handleDeleteQuestion}
                  />
                </section>
              ))
            ) : (
              <V1StyleQuestionBlocks
                stepData={stepData}
                answers={{}}
                setAnswerField={() => {}}
                readOnly
                onEditQuestion={openEditModal}
                onDeleteQuestion={handleDeleteQuestion}
              />
            )}
          </V1StyleAssessmentLayout>
        </div>
        )}
      </div>

      <EditQuestionModal
        open={editOpen}
        questionText={editQuestionText}
        saving={savingQuestion}
        onChange={setEditQuestionText}
        onClose={closeEditModal}
        onConfirm={handleUpdateQuestion}
      />

      <AddQuestionModal
        open={addOpen}
        questionText={addText}
        selectedStep={addStep}
        steps={fullSteps}
        saving={savingAdd}
        onChange={setAddText}
        onStepChange={setAddStep}
        onClose={closeAddModal}
        onConfirm={handleAddQuestion}
      />

      <BulkQuestionUploadModal
        open={bulkOpen}
        surveyId={surveyId}
        templateName={templates.find((t) => t.id === activeTemplateId)?.name || ""}
        organizationId={scopeOrgId}
        onClose={() => setBulkOpen(false)}
        onApplied={async (data) => {
          await loadTemplates(data?.id || "");
        }}
      />

      <SketchModal
        open={sketchOpen}
        organizationId={scopeOrgId}
        onClose={() => setSketchOpen(false)}
        onCreated={async (info) => {
          await loadTemplates(info?.template_id || "");
          onAssessmentCreated?.();
        }}
        onOpenEditor={async (templateId) => {
          setSketchOpen(false);
          if (!templateId) return;
          await loadTemplates(templateId);
          openEditEditor({ id: templateId });
        }}
        onUseManual={() => {
          setSketchOpen(false);
          openCreateEditor();
        }}
      />

      <AssessmentEditorModal
        open={editorOpen}
        mode={editorMode}
        templateId={editorTemplateId}
        templateMeta={editorTemplateMeta}
        systemFormatId={systemFormatId}
        organizationId={scopeOrgId}
        onClose={() => setEditorOpen(false)}
        onSaved={handleEditorSaved}
      />

    </div>
  );
}
